import React, { useState } from "react";
import {
  Button,
  Drawer,
  Typography,
  Avatar,
  Popover,
  Popconfirm,
  Tag,
  Tooltip,
  Dropdown,
  theme
} from "antd";
import {
  DeleteOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  EllipsisOutlined
} from "@ant-design/icons";
import UpdateTicketDrawer from "./UpdateTicketDrawer";
import GeneralTicketDrawer from "./GeneralTicketDrawer";
import TTTable from "./TTTable";
import AddTicket from "./AddTicket";
import { formatTicketValue } from "./ticketFields";
import axios from "axios";
import { Error, Success } from "../../../commons/Messages/Messages";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./ticket.css";

import { useTranslation } from "react-i18next";
const { Text } = Typography;

const formatTime = totalSeconds => {
  if (!totalSeconds) return "0s";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s}s`;
};

// Ticket status → antd Tag preset and the theme color used for the card's accent edge.
const STATUS_TAG = { Finished: "success", "In Progress": "processing", Pending: "warning" };
const statusAccent = (status, token) =>
  status === "Finished"
    ? token.colorSuccess
    : status === "In Progress"
      ? token.colorPrimary
      : token.colorWarning;

const GeneralTicketTable = ({ title, fields = [], group, groupId, updateParent, Alldata }) => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [tasksDrawerOpen, setTasksDrawerOpen] = useState(false);
  // One open card menu at a time; it closes when it opens a dialog so it does not float above it.
  const [menuFor, setMenuFor] = useState(null);

  const viewTasks = id => {
    setActiveTicketId(id);
    setTasksDrawerOpen(true);
  };

  // Work done in the workflow changes the ticket's status, time and team.
  const closeTasks = () => {
    setTasksDrawerOpen(false);
    updateParent();
  };

  const deleteTicket = async id => {
    try {
      const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/tickets/delete/${id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      Success(t(res.data.successMessage));
      updateParent();
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const deleteGroup = async () => {
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/ticketGroups/delete/${groupId}`,
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      Success(t(res.data.successMessage));
      updateParent();
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  return (
    <div className="tkt-group">
      {/* ── GROUP HEADER ── */}
      <div className="tkt-group-header">
        <div className="tkt-group-heading">
          <div className="tkt-group-label">{t("Section")}</div>
          <div className="tkt-group-name">{title}</div>
        </div>
        <div className="tkt-group-actions">
          <GeneralTicketDrawer
            fields={fields}
            groupId={groupId}
            title={title}
            updateParent={updateParent}
          />
          {group && <AddTicket group={group} updateParent={updateParent} />}
          <Popconfirm
            title={t("Delete this group and all its tickets?")}
            onConfirm={deleteGroup}
            okText={t("Delete")}
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              {t("Remove Group")}
            </Button>
          </Popconfirm>
        </div>
      </div>

      {/* ── TICKET CARDS ── */}
      {Alldata.length === 0 ? (
        <div className="tkt-empty">
          <OrderedListOutlined className="tkt-empty__icon" />
          <p>{t("No tickets yet — add one above!")}</p>
        </div>
      ) : (
        <div className="tkt-card-grid">
          {Alldata.map(ticket => {
            const tStatus = ticket.dynamicStatus || "Pending";

            /* Dropdown contents */
            const dropdownMenu = (
              <div className="tkt-dropdown-menu">
                <UpdateTicketDrawer
                  onOpen={() => setMenuFor(null)}
                  ticketdata={ticket}
                  fields={fields}
                  title={title}
                  ticketid={ticket._id}
                  updateParent={updateParent}
                />
                <Popconfirm
                  title={t("Delete this ticket permanently?")}
                  onConfirm={() => deleteTicket(ticket._id)}
                  okText={t("Delete")}
                  okButtonProps={{ danger: true }}
                >
                  <Button type="text" danger block icon={<DeleteOutlined />}>
                    {t("Delete")}
                  </Button>
                </Popconfirm>
              </div>
            );

            return (
              <div
                key={ticket._id}
                className="tkt-card"
                style={{ borderInlineStartColor: statusAccent(tStatus, token) }}
              >
                {/* ── TOP ROW: title + badge ── */}
                <div className="tkt-card-toprow">
                  <div className="tkt-card-heading">
                    <div className="tkt-card-title" title={ticket.title}>
                      {ticket.title}
                    </div>
                    <div className="tkt-card-time">
                      {formatTime(ticket.totalWorkTime)} {t("tracked")}
                      {ticket.taskCount > 0 &&
                        ` · ${ticket.finishedTaskCount}/${ticket.taskCount} ${t("Tasks")}`}
                    </div>
                  </div>
                  <Tag color={STATUS_TAG[tStatus] || "warning"} bordered={false}>
                    {t(tStatus)}
                  </Tag>
                </div>

                {fields.length > 0 && (
                  <div className="tkt-card-fields">
                    {fields.map(field => {
                      const value = formatTicketValue(field, ticket.ticketValues?.[field.name]);
                      return (
                        <div key={field.name} className="tkt-card-field">
                          <span title={field.name}>{field.name}</span>
                          <span title={value}>{value}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <hr className="tkt-card-divider" />

                {/* ── FOOTER: team + action buttons ── */}
                <div className="tkt-card-footer">
                  {/* Team avatars: tap or hover shows each member's tasks */}
                  <div className="tkt-card-team">
                    <div className="tkt-team-label">{t("Team")}</div>
                    {!ticket.teamDetails || ticket.teamDetails.length === 0 ? (
                      <Text type="secondary" italic className="tkt-team-empty">
                        {t("Unassigned")}
                      </Text>
                    ) : (
                      <Avatar.Group max={{ count: 5 }} size="small">
                        {ticket.teamDetails.map((member, i) => (
                          <Popover
                            key={i}
                            trigger={["hover", "click"]}
                            title={<Text strong>{member.name}</Text>}
                            content={
                              <div className="tkt-member-tasks">
                                {member.tasks.map((task, idx) => (
                                  <div key={idx} className="tkt-member-task">
                                    <div className="tkt-member-task__title">{task.title}</div>
                                    <div className="tkt-member-task__time">
                                      {formatTime(task.time)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            }
                          >
                            <Avatar
                              className="tkt-member-avatar"
                              style={{
                                backgroundColor: token.colorPrimary,
                                border: `2px solid ${token.colorBgContainer}`
                              }}
                            >
                              {member.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </Popover>
                        ))}
                      </Avatar.Group>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="tkt-card-actions">
                    <Tooltip title={t("View Workflow")}>
                      <Button
                        type="primary"
                        icon={<OrderedListOutlined />}
                        aria-label={t("View Workflow")}
                        onClick={() => viewTasks(ticket._id)}
                      />
                    </Tooltip>
                    <Dropdown
                      dropdownRender={() => dropdownMenu}
                      trigger={["click"]}
                      placement="bottomRight"
                      open={menuFor === ticket._id}
                      onOpenChange={open => setMenuFor(open ? ticket._id : null)}
                    >
                      <Button icon={<EllipsisOutlined />} aria-label={t("Actions")} />
                    </Dropdown>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── WORKFLOW DRAWER ── */}
      <Drawer
        title={
          <span className="tkt-drawer-title">
            <UnorderedListOutlined style={{ color: token.colorPrimary }} />
            <Text strong>{t("Workflow Board")}</Text>
          </span>
        }
        // Leaves the app visible behind the mask on wide screens; full width on phones.
        width={isMobile ? "100%" : "min(1480px, calc(100vw - 72px))"}
        rootClassName="tkt-workflow-drawer"
        onClose={closeTasks}
        open={tasksDrawerOpen}
        destroyOnClose
        styles={{ body: { backgroundColor: token.colorBgLayout } }}
      >
        <TTTable
          ticketid={activeTicketId}
          ticketTitle={Alldata.find(ticket => ticket._id === activeTicketId)?.title}
        />
      </Drawer>
    </div>
  );
};

export default GeneralTicketTable;
